import { createSlice } from '@reduxjs/toolkit';
import {
  addFrontPartInspection,
  addLavSideWallInspection,
  addShowerTestingInspection,
  fetchFrontPartInspections,
  fetchLavSideWallInspections,
  fetchShowerTestingInspections,
  getDailyShellReport,
  getDailyShellReportList,
  getMonthlyInspectionReportList,
  getMonthlyShellReportList,
} from './frontpartThunk';

const initialState = {
  front_part_inspections: [],
  lav_side_wall_inspections: [],
  current_inspection: null,
  stages: [],
  shower_testing_inspections: {
    loading: false,
    success: false,
    error: null,
    data: [],
    pagination: {
      currentPage: 1,
      total: 1,
      totalItems: 0,
      per_page: 10,
    },
  },
  shell_report: {
    loading: false,
    data: [],
    error: null,
  },
  shell_report_list: {
    loading: false,
    data: [],
    error: null,
  },
  monthly_shell_report_list: {
    loading: false,
    data: [],
    error: null,
  },
  monthly_inspection_report_list: {
    loading: false,
    data: [],
    summary: {},
    error: null,
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      total_pages: 1,
    },
  },
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  },
  loading: false,
  error: null,
};

const frontpartSlice = createSlice({
  name: 'frontpart',
  initialState,
  reducers: {
    clearCurrentInspection: (state) => {
      state.current_inspection = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all inspections
      .addCase(fetchFrontPartInspections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFrontPartInspections.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.success) {
          state.front_part_inspections = action.payload.data || [];

          state.pagination = {
            current_page: action.payload.current_page || 1,
            per_page: action.payload.per_page || 10,
            total: action.payload.total || 0,
            last_page: action.payload.total_pages || 1,
          };
        }
      })
      .addCase(fetchFrontPartInspections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch inspections';
      })

      // Add/Update inspection
      .addCase(addFrontPartInspection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFrontPartInspection.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addFrontPartInspection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Operation failed';
      })
      .addCase(fetchShowerTestingInspections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShowerTestingInspections.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.success) {
          state.shower_testing_inspections.data = action.payload.data || [];

          state.shower_testing_inspections.pagination = {
            current_page: action.payload.current_page || 1,
            per_page: action.payload.per_page || 10,
            total: action.payload.total || 0,
            total_pages: action.payload.total_pages || 1,
          };
        }
      })
      .addCase(fetchShowerTestingInspections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add/Update inspection
      .addCase(addShowerTestingInspection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addShowerTestingInspection.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addShowerTestingInspection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getDailyShellReport.pending, (state) => {
        state.shell_report.loading = true;
        state.shell_report.error = null;
      })
      .addCase(getDailyShellReport.fulfilled, (state, action) => {
        state.shell_report.loading = false;
        state.shell_report.data = action.payload.data;
      })
      .addCase(getDailyShellReport.rejected, (state, action) => {
        state.shell_report.loading = false;
        state.shell_report.error = action.payload;
      })

      .addCase(getDailyShellReportList.pending, (state) => {
        state.shell_report_list.loading = true;
        state.shell_report_list.error = null;
      })
      .addCase(getDailyShellReportList.fulfilled, (state, action) => {
        state.shell_report_list.loading = false;
        state.shell_report_list.data = action.payload.data;
      })
      .addCase(getDailyShellReportList.rejected, (state, action) => {
        state.shell_report_list.loading = false;
        state.shell_report_list.error = action.payload;
      })

      .addCase(getMonthlyShellReportList.pending, (state) => {
        state.monthly_shell_report_list.loading = true;
        state.monthly_shell_report_list.error = null;
      })
      .addCase(getMonthlyShellReportList.fulfilled, (state, action) => {
        state.monthly_shell_report_list.loading = false;
        state.monthly_shell_report_list.data = action.payload.data;
      })
      .addCase(getMonthlyShellReportList.rejected, (state, action) => {
        state.monthly_shell_report_list.loading = false;
        state.monthly_shell_report_list.error = action.payload;
      })
      // Add/Update inspection
      .addCase(addLavSideWallInspection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addLavSideWallInspection.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addLavSideWallInspection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Operation failed';
      })
      // Fetch all inspections
      .addCase(fetchLavSideWallInspections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLavSideWallInspections.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.success) {
          state.lav_side_wall_inspections = action.payload.data || [];

          state.pagination = {
            current_page: action.payload.current_page || 1,
            per_page: action.payload.per_page || 10,
            total: action.payload.total || 0,
            last_page: action.payload.total_pages || 1,
          };
        }
      })
      .addCase(fetchLavSideWallInspections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch inspections';
      })
      .addCase(getMonthlyInspectionReportList.pending, (state) => {
        state.monthly_inspection_report_list.loading = true;
        state.monthly_inspection_report_list.error = null;
      })
      .addCase(getMonthlyInspectionReportList.fulfilled, (state, action) => {
        state.monthly_inspection_report_list.loading = false;
        state.monthly_inspection_report_list.data = action.payload?.data?.data || [];

        state.monthly_inspection_report_list.summary = action.payload?.data?.summary || {};
        state.monthly_inspection_report_list.pagination = {
          current_page: action.payload?.data?.page || 1,
          per_page: action.payload?.data?.per_page || 10,
          total: action.payload?.data?.total || 0,
          total_pages: action.payload?.data?.last_page || 1,
        };
      })
      .addCase(getMonthlyInspectionReportList.rejected, (state, action) => {
        state.monthly_inspection_report_list.loading = false;
        state.monthly_inspection_report_list.error =
          action.payload?.message || 'Failed to fetch report';
      });
  },
});

export const { clearCurrentInspection, clearError } = frontpartSlice.actions;
export default frontpartSlice.reducer;
